import { useMemo } from "react";
import { Plus, Check, X } from "lucide-react";
import { formatMXN } from "../../../lib/whatsapp";
import { PACKAGE_CATEGORIES } from "./constants";
import { AlbumConfigurator } from "./AlbumConfigurator";

const PackageDetailsLine = ({ pkg }) => {
  const details = useMemo(
    () =>
      [
        pkg.hours > 0 ? `${pkg.hours}h de cobertura` : null,
        pkg.photos_count > 0 ? `${pkg.photos_count} fotos` : null,
        pkg.videos_count > 0 ? `${pkg.videos_count} videos` : null,
        pkg.clips_count > 0 ? `${pkg.clips_count} clips` : null,
        pkg.video_master_count > 0 ? `${pkg.video_master_count} Video Master` : null,
      ]
        .filter(Boolean)
        .join(" · "),
    [pkg.hours, pkg.photos_count, pkg.videos_count, pkg.clips_count, pkg.video_master_count]
  );

  if (!details) return null;
  return <p className="text-white/50 text-xs mb-1">{details}</p>;
};

export const PackagesStep = ({ activeCategory, setActiveCategory, categoryPackages, isSelected, togglePackage, selectedPackages, albumConfig }) => (
  <div className="space-y-6">
    <h3 className="font-serif-display text-2xl text-white mb-2">Elige tus paquetes</h3>

    <div className="flex flex-wrap gap-2">
      {PACKAGE_CATEGORIES.map((c) => (
        <button
          key={c.key}
          data-testid={`quote-category-tab-${c.key}`}
          onClick={() => setActiveCategory(c.key)}
          className={`px-4 py-2 text-xs uppercase tracking-[0.1em] border transition-colors ${
            activeCategory === c.key ? "bg-twp-gold text-black border-twp-gold" : "border-white/20 text-white/60 hover:border-twp-gold/60"
          }`}
        >
          {c.label}
        </button>
      ))}
    </div>

    {activeCategory === "album_impreso" ? (
      <div data-testid="quote-album-main-tab">
        <p className="text-white/50 text-sm mb-4">
          Diseña tu álbum impreso paso a paso: tipo, tamaño, portada y color. Se sumará a tu presupuesto total.
        </p>
        <AlbumConfigurator config={albumConfig} />
      </div>
    ) : (
      <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
        {categoryPackages.length === 0 ? (
          <p className="text-white/40 text-sm">No hay paquetes disponibles en esta categoría todavía.</p>
        ) : (
          categoryPackages.map((pkg) => (
            <div
              key={pkg.id}
              data-testid={`quote-package-card-${pkg.id}`}
              className={`border p-4 flex items-start justify-between gap-4 transition-colors ${isSelected(pkg.id) ? "border-twp-gold bg-twp-gold/5" : "border-white/10"}`}
            >
              <div>
                <p className="text-white font-serif-display text-lg">{pkg.name}</p>
                <PackageDetailsLine pkg={pkg} />
                <p className="text-white/60 text-sm">{pkg.description}</p>
                <p className="text-twp-gold text-sm mt-2">{formatMXN(pkg.price)}</p>
              </div>
              <button
                data-testid={`quote-package-toggle-${pkg.id}`}
                onClick={() => togglePackage(pkg)}
                className={`shrink-0 flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-wider ${
                  isSelected(pkg.id) ? "bg-twp-gold text-black" : "border border-white/30 text-white/80"
                }`}
              >
                {isSelected(pkg.id) ? <Check size={14} /> : <Plus size={14} />}
                {isSelected(pkg.id) ? "Agregado" : "Agregar"}
              </button>
            </div>
          ))
        )}
      </div>
    )}

    {selectedPackages.length > 0 && (
      <div className="border-t border-white/10 pt-4" data-testid="quote-selected-packages">
        <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Paquetes seleccionados</p>
        <div className="space-y-2">
          {selectedPackages.map((p) => (
            <div key={p.id} className="flex items-center justify-between text-sm text-white/80">
              <span>{p.name} ({PACKAGE_CATEGORIES.find((c) => c.key === p.category)?.label})</span>
              <div className="flex items-center gap-3">
                <span className="text-twp-gold">{formatMXN(p.price)}</span>
                <button data-testid={`quote-package-remove-${p.id}`} onClick={() => togglePackage(p)} className="text-white/40 hover:text-red-400">
                  <X size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);
